import React, { useState, useCallback, useMemo, useEffect } from "react";
import LoadingIndicator from "react-loading-indicator";
import _ from "lodash";
import { SidePanel, SearchBar } from "../Components";
import useComponentVisible from "../../../../hooks/useComponentVisible";
import { noop, searchOptions, getRandomId } from "../utils";
import { withRefluxStore, PreviewStore } from "../../../../Stores";
import Actions from "../../../../Actions";
import { CustomerSucessController } from "@talentbait/shared-components";
import { PageWrapper, PageSelectorWrapper, Editor, SearchBarWrapper, Pages, Page } from "./styles";
import logger from "../../../../utils/LoggerFrontend";

const MAX_PAGE_RESULT = 30;

const PageSelector = ({
  updateChannelData,
  pageId,
  children,
  editable,
  dark,
  // Store
  facebookPages,
  loadingFacebookPages,
  campaignData,
  userProfile
}) => {
  const defaultPage = useMemo(
    () => (pageId != null ? facebookPages.find(({ id }) => id === pageId) : null),
    [facebookPages, pageId]
  );
  const [isChangedAnimation, setIsChangedAnimation] = useState(false);
  const [selectedPage, setSelectedPage] = useState(defaultPage);
  const [searchText, setSearchText] = useState("");

  // Filter and sort page options based on search text and public page status and limit the
  // results to the first 10 pages.
  const filteredPages = useMemo(() => {
    const result = searchOptions({ options: facebookPages, searchText: searchText, key: "name" })
      .filter((facebookPage) => !facebookPage.onlyAdmin)
      .slice(0, MAX_PAGE_RESULT)
      .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
    // If the currently selected page is not there, then add it
    if (!_.find(result, { id: pageId })) {
      result.unshift(_.find(facebookPages, { id: pageId }) || {});
    }
    return result;
  }, [facebookPages, searchText, pageId]);

  useEffect(() => {
    setSelectedPage(defaultPage);
  }, [defaultPage]);

  const closeEditingCallback = useCallback(() => {
    setSelectedPage(defaultPage);
  }, [defaultPage]);

  const {
    ref: editorRef,
    isComponentVisible: editing,
    setIsComponentVisible: setEditing
  } = useComponentVisible(false, closeEditingCallback);

  const selectPage = useCallback(
    (page) => {
      updateChannelData({ key: "pageName", value: page.name });
      updateChannelData({ key: "pageId", value: page.id });
      updateChannelData({
        key: "pageLogo",
        value: `http://graph.facebook.com/${page.id}/picture?type=square`
      });
      CustomerSucessController.send(`FacebookPageChanged`, {
        campaignId: campaignData?.id,
        companyName: campaignData?.orgaName,
        newFacebookPage: page.name,
        oldFacebookPage: selectedPage?.name,
        userName: userProfile.userName,
        orgaId: campaignData?.organizationId,
        title: campaignData?.jobTitle
      });
      Actions.logCampaignActivity(campaignData.id, {
        message: `Changed Facebook Page`,
        type: "Change",
        orgaId: campaignData.organizationId,
        date: new Date().toISOString(),
        userName: userProfile.userName
      });
      return;
    },
    [updateChannelData, campaignData, userProfile, selectedPage]
  );

  const stopIsChangedAnimation = useCallback(() => setIsChangedAnimation(false), []);

  const openEditor = useCallback(() => {
    setEditing(true);
  }, [setEditing]);

  const tooltipId = useMemo(() => getRandomId(), []);

  // This feature is currently disabled
  const isPageEditable = useMemo(
    () => editable && campaignData?.id && campaignData.status != "DRAFT",
    [campaignData, editable]
  );

  logger.debug("PageSelectorClient", "got campaign data", {
    campaignData,
    userProfile,
    isPageEditable
  });

  return (
    <PageSelectorWrapper isChangedAnimation={isChangedAnimation} dark={dark}>
      <PageWrapper
        onAnimationEnd={stopIsChangedAnimation}
        editable={isPageEditable}
        onClick={isPageEditable ? openEditor : noop}
        dark={dark}
        data-tip=""
        data-for={tooltipId}
      >
        {selectedPage
          ? React.cloneElement(children, {
              ...children.props,
              pageLogo: `http://graph.facebook.com/${selectedPage.id}/picture?type=square`,
              pageName: selectedPage.name,
              pageId: selectedPage.id
            })
          : children}
      </PageWrapper>
      <SidePanel
        active={editing}
        alwaysVisible
        side="left"
        height="280px"
        offset={6}
        padding="0 0 5px 0"
      >
        <Editor ref={editorRef}>
          <SearchBarWrapper>
            <SearchBar onChange={setSearchText} disabled={loadingFacebookPages} />
          </SearchBarWrapper>
          <Pages>
            {loadingFacebookPages ? (
              <LoadingIndicator />
            ) : (
              filteredPages.map((page) => (
                <Page
                  key={`${page.id}_${pageId}`}
                  onClick={() => selectPage(page)}
                  selected={selectedPage?.id === page.id}
                >
                  {page.name}
                </Page>
              ))
            )}
          </Pages>
        </Editor>
      </SidePanel>
    </PageSelectorWrapper>
  );
};

export default withRefluxStore(
  PageSelector,
  [PreviewStore],
  ["facebookPages", "loadingFacebookPages", "campaignData", "userProfile"]
);
